// src/components/vault/documents/id-validation/ProofOfAddressValidationLoader.tsx

import React from "react";
import { motion, AnimatePresence } from "motion/react";
import { CheckCircle2, FileText, Home } from "lucide-react";
import { cn } from "../../../../lib/utils";
import { PROOF_OF_ADDRESS_VALIDATION_STEPS, PROOF_OF_ADDRESS_TYPE_LABELS } from "./constants";
import { ValidationStepsList } from "./IDValidationLoader";

interface ProofOfAddressValidationLoaderProps {
  progress: number;
  documentType?: string;
}

export const ProofOfAddressValidationLoader: React.FC<ProofOfAddressValidationLoaderProps> = ({
  progress,
  documentType,
}) => {
  const isComplete = progress >= 100;
  const documentLabel = documentType
    ? PROOF_OF_ADDRESS_TYPE_LABELS[documentType] || documentType
    : undefined;

  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 w-full max-w-sm mx-auto">
      {/* Document card with scanning effect */}
      <div className="relative mb-8">
        {!isComplete && (
          <div className="absolute -inset-3 rounded-3xl bg-[#28397a]/5 blur-xl animate-pulse" />
        )}

        <motion.div
          className={cn(
            "relative w-44 h-28 rounded-2xl border-2 overflow-hidden flex items-center justify-center",
            isComplete
              ? "border-emerald-300 bg-emerald-50 shadow-lg shadow-emerald-100"
              : "border-[#28397a]/20 bg-white shadow-xl shadow-[#28397a]/10"
          )}
          animate={
            isComplete
              ? { borderColor: "rgb(110 231 183)", backgroundColor: "rgb(236 253 245)" }
              : {}
          }
          transition={{ duration: 0.7 }}
        >
          {!isComplete && (
            <div
              className="absolute inset-x-0 h-8 bg-gradient-to-b from-[#28397a]/10 via-[#28397a]/5 to-transparent pointer-events-none"
              style={{ animation: "scanLine 2s ease-in-out infinite" }}
            />
          )}

          <AnimatePresence mode="wait">
            {!isComplete ? (
              <motion.div
                key="scanning"
                className="flex items-center gap-3 px-4"
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                <div className="w-10 h-12 rounded-lg bg-[#28397a]/8 flex items-center justify-center flex-shrink-0">
                  <Home className="w-5 h-5 text-[#28397a]/40" strokeWidth={1.5} />
                </div>
                <div className="flex flex-col gap-1.5 flex-1">
                  <div className="h-2 w-16 rounded-full bg-[#28397a]/10" />
                  <div className="h-1.5 w-12 rounded-full bg-[#28397a]/6" />
                  <div className="h-1.5 w-20 rounded-full bg-[#28397a]/6" />
                  <div className="h-1.5 w-8 rounded-full bg-[#28397a]/6" />
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="complete"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.3 }}
              >
                <CheckCircle2
                  className="w-12 h-12 text-emerald-500"
                  strokeWidth={1.5}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Title and badge */}
      <div className="flex flex-col items-center mb-2">
        <AnimatePresence mode="wait">
          <motion.h3
            key={isComplete ? "done" : "loading"}
            className="text-xl font-semibold text-slate-900 mb-1.5 text-center tracking-tight"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
          >
            {isComplete ? "Validacion completada" : "Validando comprobante"}
          </motion.h3>
        </AnimatePresence>
        {documentLabel && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-xs font-medium text-slate-500">
            <FileText className="w-3 h-3" />
            {documentLabel}
          </span>
        )}
      </div>

      {/* Stepped progress */}
      <ValidationStepsList
        steps={PROOF_OF_ADDRESS_VALIDATION_STEPS}
        progress={progress}
        isComplete={isComplete}
      />

      {/* Overall progress bar */}
      <div className="w-full max-w-xs mx-auto mt-5">
        <div className="w-full h-1 rounded-full bg-slate-100 overflow-hidden">
          <motion.div
            className={cn(
              "h-full rounded-full",
              isComplete ? "bg-emerald-500" : "bg-[#28397a]"
            )}
            animate={{ width: `${isComplete ? 100 : progress}%` }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
        </div>
      </div>

      <style>{`
        @keyframes scanLine {
          0%, 100% { top: -2rem; }
          50% { top: calc(100% - 2rem); }
        }
      `}</style>
    </div>
  );
};
